/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

// --- Size conversions ---
// One map keyed by EU size.
// Values are final labels per system (EU, UK[=GB], US).
// Kids 21–35 keep old mapping.
// Adults 36–43 use shoebox-label mapping; 40–42 include W/M; 44–45 use Lejan men-only mapping.
const SIZE_CONVERSIONS = {
  // Kids
  "21": { EU: "21", UK: "4",   US: "5.5"  },
  "22": { EU: "22", UK: "5",   US: "6"    },
  "23": { EU: "23", UK: "6",   US: "7"    },
  "24": { EU: "24", UK: "7",   US: "8"    },
  "25": { EU: "25", UK: "7.5", US: "8.5"  },
  "26": { EU: "26", UK: "8.5", US: "9.5"  },
  "27": { EU: "27", UK: "9",   US: "10"   },
  "28": { EU: "28", UK: "10",  US: "11"   },
  "29": { EU: "29", UK: "11",  US: "12"   },
  "30": { EU: "30", UK: "11.5",US: "12.5" },
  "31": { EU: "31", UK: "12.5",US: "13.5" },
  "32": { EU: "32", UK: "13.5",US: "1Y"   },
  "33": { EU: "33", UK: "1",   US: "1.5Y" },
  "34": { EU: "34", UK: "2",   US: "2.5Y" },
  "35": { EU: "35", UK: "2.5", US: "3Y"   },

  // Adults – Lejan table (36–43)
  "36": { EU: "36", UK: "3",          US: "5.5"        },
  "37": { EU: "37", UK: "4",          US: "6"          },
  "38": { EU: "38", UK: "5",          US: "7"          },
  "39": { EU: "39", UK: "6",          US: "8"          },
  "40": { EU: "40", UK: "7W / 6M",    US: "8.5W / 7M"  },
  "41": { EU: "41", UK: "8W / 7M",    US: "9.5W / 8M"  },
  "42": { EU: "42", UK: "9W / 8M",    US: "10W / 8.5M" },
  "43": { EU: "43", UK: "9",          US: "9.5"        },

  // 44–46: only men (from previous Lejan chart; 46 matches the live size guide table)
  "44": { EU: "44", UK: "10",         US: "10"         },
  "45": { EU: "45", UK: "11",         US: "11"         },
  "46": { EU: "46", UK: "12",         US: "12"         }
};

// US kids labels carry a single trailing "K" (EU 21-35 shoes; socks ranges ending at EU 37 or below).
const KIDS_MAX_EU = 37;        // socks: last EU size of a kids range (33-37)
const KIDS_MAX_EU_SINGLE = 35; // shoes: last EU kids size
const KIDS_US_SUFFIX = 'K';

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    // Cache for dynamically parsed size conversions from the size guide table.
    // null = not yet attempted; false = attempted, no table found; object = parsed data.
    static _dynamicSizeConversions = null;

    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form');
      this.optionSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom)');
      this.nativeSelector = document.getElementById(`variants-${this.section.dataset.section}`);
      this.data = this.getVariantData();
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      // Size system (EU default; may be US/UK depending on market or user choice)
      this.sizeSystem = (this.dataset.sizeSystemDefault || 'EU').toUpperCase();
      // Range mode (true for products tagged 'socks'; set by Liquid data attribute)
      this.sizeRange = this.dataset.sizeRange === 'true';

      this.updateAvailability();
      this.updateAddToCartButton();
      this.addEventListener('change', this.handleVariantChange.bind(this));
      this.applySearchParams();

      // Init size conversion UI/labels
      this.initSizeConversion();

      this.setAttribute('loaded', '');

      try {
        this.dispatchEvent(new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            allVariants: this.data.variants,
            selectedOptions: this.selectedOptions
          }
        }));
      } catch (e) {}
    }

    /**
     * Shopify puede marcar `variant.available` como true aunque el inventario llegue a 0,
     * dependiendo de la `inventory_policy` y/o apps/wh que alteran disponibilidad.
     * Para "agotadas reales" usamos: inventory_policy === 'deny' && inventory_quantity <= 0.
     */
    isVariantPurchasable(variant) {
      if (!variant) return false;

      const hasInventoryTracking = variant.inventory_management != null && variant.inventory_management !== '';
      const policy = variant.inventory_policy;
      const qty = Number(variant.inventory_quantity);

      if (hasInventoryTracking && policy === 'deny' && !Number.isNaN(qty) && qty <= 0) {
        return false;
      }

      return !!variant.available;
    }

    /**
     * Handles 'change' events on the variant picker element.
     */
    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.updateUrl(evt);
      this.updateVariantInput();
      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateBarcode();
      VariantPicker.updateLabelText(evt);

      // Keep size labels in the chosen system after any change
      this.applySizeSystem(this.sizeSystem);

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.productForm,
          variant: this.variant,
          allVariants: this.data.variants,
          selectedOptions: this.selectedOptions
        }
      }));
    }

    updateAddToCartButton() {
      if (!this.productForm) return;
      if (this.selectedOptions.indexOf(null) > -1) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.isVariantPurchasable(this.variant);
      const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;
      const isAtcPrice = this.addBtn.classList.contains('atc-btn--with-price');

      // Safety: when inventory is tracked with "deny" policy, prevent submitting
      // quantities greater than current inventory. This avoids Shopify 422 errors.
      if (this.variant && this.productForm) {
        const qtyInput = this.productForm.querySelector('input[name="quantity"]');
        const invQty = Number(this.variant.inventory_quantity);
        const policy = this.variant.inventory_policy;
        const mgmt = this.variant.inventory_management;

        if (
          qtyInput &&
          mgmt != null &&
          mgmt !== '' &&
          policy === 'deny' &&
          !Number.isNaN(invQty) &&
          invQty >= 0
        ) {
          const currentQty = Number(qtyInput.value);
          if (!Number.isNaN(currentQty) && currentQty > invQty) {
            qtyInput.value = String(invQty);
          }
        }
      }

      this.addBtn.disabled = !variantAvailable;

      if (isAtcPrice) {
        /* Guardar HTML original la primera vez */
        if (!this.addBtn.dataset.atcOriginalHtml) {
          this.addBtn.dataset.atcOriginalHtml = this.addBtn.innerHTML;
        }

        if (variantAvailable && this.variant) {
          /* Restaurar estructura si fue destruida (ej. variante agotada previa) */
          if (!this.addBtn.querySelector('[data-atc-btn-price]')) {
            this.addBtn.innerHTML = this.addBtn.dataset.atcOriginalHtml;
          }
          var formattedPrice = this.data.formatted && this.data.formatted[this.variant.id]
            ? this.data.formatted[this.variant.id].price
            : '';
          var priceEl = this.addBtn.querySelector('[data-atc-btn-price]');
          if (priceEl) priceEl.innerHTML = formattedPrice;
        } else {
          /* Variante agotada: texto plano */
          this.addBtn.textContent = unavailableStr;
        }
      } else {
        this.addBtn.textContent = variantAvailable
          ? this.addBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    updateAvailability() {
      if (this.dataset.availability === 'off') return;
      const { availabilityMode } = this.dataset;
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMode === 'selection') {
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                const purchasable = this.isVariantPurchasable(variant);
                const qty = Number(variant.inventory_quantity);
                const isLowStock = !!(
                  purchasable &&
                  variant.inventory_management &&
                  !Number.isNaN(qty) &&
                  qty > 0 &&
                  qty < 15
                );

                VariantPicker.updateOptionAvailability(
                  optionEl,
                  true,
                  purchasable,
                  isLowStock
                );
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue =
              selector.dataset.selectorType === 'dropdown'
                ? option.dataset.value
                : option.value;

            let variantsExist = false;
            let variantsAvailable = false;
            let lowStock = false;

            this.data.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }

              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (this.isVariantPurchasable(v)) {
                  variantsAvailable = true;
                  const qty = Number(v.inventory_quantity);
                  if (
                    v.inventory_management &&
                    !Number.isNaN(qty) &&
                    qty > 0 &&
                    qty < 15
                  ) {
                    lowStock = true;
                  }
                }
              }
            });

            VariantPicker.updateOptionAvailability(
              option,
              variantsExist,
              variantsAvailable,
              lowStock
            );
          });
        });
      }
    }

    static updateOptionAvailability(optionEl, exists, available, lowStock = false) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      const lowStockText = 'Poco stock';

      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');
        let textToInsert = '';

        if (!exists || !available) {
          textToInsert = unavailableText;
        } else if (lowStock) {
          textToInsert = lowStockText;
        }

        if (textToInsert) {
          if (em) {
            em.hidden = false;
            em.textContent = textToInsert;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${textToInsert}</em>`;
          }
        } else if (em) {
          em.hidden = true;
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
        if (lowStock) {
          el.nextElementSibling.title = lowStockText;
        }
      }
    }

    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default') ? '' : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    updatePickupAvailability() {
      this.pickUpAvailability = this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-price .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.variants[0];
        for (let i = 1; i < this.data.variants.length; i += 1) {
          if (this.data.variants[i].price < variant.price) variant = this.data.variants[i];
        }
      }

      if (variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        priceCurrentEl.innerHTML = this.data.formatted[variant.id].price;
        if (priceWasEl) priceWasEl.innerHTML = this.data.formatted[variant.id].compareAtPrice || '';

        if (variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = variant.unit_price_measurement.reference_value;
          const unit = variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
          unitPriceEl.hidden = false;
        } else if (unitPriceEl) {
          unitPriceEl.hidden = true;
        }

        this.price.classList.toggle('price--on-sale', variant.compare_at_price > variant.price);
        this.price.classList.toggle('price--sold-out', !variant.available && !this.preSelection);
      }

      this.price.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
      this.price.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    updateBarcode() {
      this.barcode = this.barcode || this.section.querySelector('.product-barcode__value');
      if (!this.barcode) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
      this.barcode.parentNode.hidden = !barcodeAvailable;
    }

    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      const url = this.variant ? `${this.dataset.url}?variant=${this.variant.id}` : this.dataset.url;
      window.history.replaceState({}, '', url);
    }

    updateVariantInput() {
      this.forms = this.forms || this.section.querySelectorAll('.js-product-form, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('[name="id"]');
        input.value = this.variant ? this.variant.id : '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    getVariantData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    getSelectedVariant() {
      return this.data.variants.find(
        (v) => v.options.every((val, index) => val === this.selectedOptions[index])
      );
    }

    applySearchParams() {
      const searchParams = new URLSearchParams(window.location.search);

      Array.from(searchParams.keys()).forEach((key) => {
        const value = searchParams.get(key);
        const optionSelectors = Array.from(this.optionSelectors);
        const matchingOptionSelector = optionSelectors.find((x) => x.dataset.option === key);
        if (!matchingOptionSelector) return;

        if (matchingOptionSelector.dataset.selectorType === 'dropdown') {
          const colorOptionDropdown = matchingOptionSelector.querySelector(`.custom-select__option[data-value="${value}"]`);
          if (colorOptionDropdown) {
            const customSelect = colorOptionDropdown.closest('custom-select');
            customSelect.selectOption(colorOptionDropdown);
          }
        } else {
          const matchingInput = matchingOptionSelector.querySelector(`input[value="${value}"]`);
          if (matchingInput) {
            const matchingOptionValue = matchingOptionSelector.querySelector('.option-selector__label-value');
            if (matchingOptionValue) {
              matchingOptionSelector.querySelector('.option-selector__label-value').textContent = value;
            }
            matchingInput.checked = true;
            matchingInput.dispatchEvent(
              new CustomEvent('change', { bubbles: true, cancelable: false })
            );
          }
        }
      });
    }

    // -----------------------
    // EU/GB/US label converter (supports ranges like "21-24")
    // -----------------------

    initSizeConversion() {
      // Only proceed if there is at least one size selector marked by Liquid
      this.sizeSelectors = Array.from(this.optionSelectors).filter((sel) =>
        sel.hasAttribute('data-size-option')
      );
      if (this.sizeSelectors.length === 0) return;

      // Respect a previous user choice (persisted), if any
      try {
        const stored = localStorage.getItem('sizeSystem');
        if (stored && ['EU', 'US', 'UK'].includes(stored)) this.sizeSystem = stored;
      } catch (e) {}

      // Activate correct button state
      this.setActiveSizeSystem(this.sizeSystem);

      // Wire up the toggle buttons
      this.querySelectorAll('.js-size-system-toggle').forEach((btn) => {
        btn.addEventListener('click', () => {
          const system = btn.dataset.system;
          this.sizeSystem = system;
          this.setActiveSizeSystem(system);
          this.applySizeSystem(system);
          try { localStorage.setItem('sizeSystem', system); } catch (e) {}
        });
      });

      // Initial paint
      this.applySizeSystem(this.sizeSystem);

      // Re-apply if options are re-rendered by other logic
      this.addEventListener('on:variant:change', () => {
        this.applySizeSystem(this.sizeSystem);
      });
    }

    setActiveSizeSystem(system) {
      this.querySelectorAll('.js-size-system-toggle').forEach((b) => {
        const active = b.dataset.system === system;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    applySizeSystem(system) {
      if (!this.sizeSelectors || this.sizeSelectors.length === 0) return;

      this.sizeSelectors.forEach((selector) => {
        const type = selector.dataset.selectorType;

        if (type === 'dropdown') {
          const options = selector.querySelectorAll('.custom-select__option');
          options.forEach((opt) => {
            const src = opt.dataset.value || opt.textContent;
            if (!src) return;
            const label = VariantPicker.labelForEUValue(src, system, this.sizeRange);

            const slot = opt.querySelector('.js-value, .custom-select__label, span');
            if (slot) {
              slot.textContent = label;
            } else {
              opt.textContent = label;
            }
          });

          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          if (selected) {
            const selectedSrc = selected.dataset.value || selected.textContent;
            const selectedLabel = VariantPicker.labelForEUValue(
              selectedSrc,
              system,
              this.sizeRange
            );

            const customSelect = selected.closest('custom-select');
            if (customSelect && typeof customSelect.selectOption === 'function') {
              customSelect.selectOption(selected);
            }

            this.updateCustomSelectTriggerText(selector, selectedLabel);

            if (customSelect && typeof customSelect.selectOption !== 'function') {
              selected.dispatchEvent(new MouseEvent('click', { bubbles: true }));
            }
          }
        } else {
          const inputs = selector.querySelectorAll('input.js-option');
          inputs.forEach((input) => {
            const src = input.value;
            const label = VariantPicker.labelForEUValue(src, system, this.sizeRange);
            const lbl = input.nextElementSibling;
            const span = lbl && (lbl.querySelector('.js-value') || lbl);
            if (span) span.textContent = label;
          });
        }
      });
    }

    updateCustomSelectTriggerText(selector, label) {
      const triggerCandidates = [
        '.custom-select__toggle',
        '.custom-select__button',
        '.custom-select__btn',
        '.custom-select__selected',
        '.custom-select__display',
        '.custom-select__trigger',
        'button[aria-haspopup="listbox"]',
        'button'
      ];

      let trigger = null;
      for (let i = 0; i < triggerCandidates.length; i += 1) {
        const found = selector.querySelector(triggerCandidates[i]);
        if (found) { trigger = found; break; }
      }
      if (!trigger) return;

      const textNode =
        trigger.querySelector('.js-value') ||
        trigger.querySelector('.custom-select__selected-value') ||
        trigger.querySelector('.custom-select__label') ||
        trigger.querySelector('span') ||
        trigger;

      if (textNode) textNode.textContent = label;

      trigger.setAttribute('data-current-label', label);
      trigger.setAttribute('aria-label', label);
    }

    // --- Helpers for parsing & labeling (range-aware) ---

    static parseEUFromValue(str) {
      if (!str) return null;
      const s = String(str).trim();

      // match "21-24", "21 – 24", "21—24" (hyphen/en dash/em dash, optional spaces)
      const m = s.match(/(\d+(?:\.\d+)?)\s*([–—-])\s*(\d+(?:\.\d+)?)/);
      if (m) {
        const start = m[1].replace(/\.0$/, '');
        const end = m[3].replace(/\.0$/, '');
        const sep = m[2] || '-';
        return { isRange: true, start, end, sep };
      }

      const n = s.match(/\d+(?:\.\d+)?/);
      if (!n) return null;
      return { isRange: false, key: n[0].replace(/\.0$/, '') };
    }

    /**
     * Attempts to parse EU/GB/US size conversions from the size guide table.
     * Looks first in <template> content fragments (available before DOMContentLoaded),
     * then in the live DOM (for cases where the template has already been cloned).
     * Returns an object keyed by EU size string, or null if no table is found.
     */
    static parseSizeGuideTable() {
      let container = null;

      // Strategy 1: Search inside <template> content fragments.
      // The guia-tallas-artu section wraps its modal in a <template> element
      // whose content DocumentFragment is accessible as soon as the HTML is parsed.
      const templates = document.querySelectorAll('template[id^="artu-size-guide-modal-"]');
      for (let i = 0; i < templates.length; i++) {
        const frag = templates[i].content;
        const candidate = frag.querySelector('[data-size-guide-table]');
        if (candidate) {
          container = candidate;
          break;
        }
      }

      // Strategy 2: If the template has already been cloned to <body>, search the live DOM.
      if (!container) {
        container = document.querySelector('[data-size-guide-table]');
      }

      if (!container) return null;

      const table = container.querySelector('table');
      if (!table) return null;

      const thead = table.querySelector('thead');
      const tbody = table.querySelector('tbody');
      if (!thead || !tbody) return null;

      // Identify the EU, GB/UK, and US columns by header text.
      const ths = thead.querySelectorAll('th');
      let euColIdx = -1;
      let gbColIdx = -1;
      let usColIdx = -1;

      for (let i = 0; i < ths.length; i++) {
        const text = (ths[i].textContent || '').trim().toUpperCase();
        if (text === 'EU' && euColIdx === -1)                       euColIdx = i;
        else if ((text === 'GB' || text === 'UK') && gbColIdx === -1) gbColIdx = i;
        else if (text === 'US' && usColIdx === -1)                  usColIdx = i;
      }

      // We need at minimum the EU column plus at least one of GB or US.
      if (euColIdx === -1) return null;
      if (gbColIdx === -1 && usColIdx === -1) return null;

      // Parse all rows from <tbody>.
      const rows = tbody.querySelectorAll('tr');
      const conversions = {};

      for (let r = 0; r < rows.length; r++) {
        const cells = rows[r].querySelectorAll('td');

        const euRaw = cells[euColIdx] ? cells[euColIdx].textContent.trim() : '';
        if (!euRaw) continue;

        // Normalize EU key: remove trailing ".0", trim whitespace.
        const euKey = euRaw.replace(/\.0$/, '');

        const entry = { EU: euKey };

        if (gbColIdx !== -1 && cells[gbColIdx]) {
          entry.UK = cells[gbColIdx].textContent.trim();
        }
        if (usColIdx !== -1 && cells[usColIdx]) {
          entry.US = cells[usColIdx].textContent.trim();
        }

        conversions[euKey] = entry;
      }

      return Object.keys(conversions).length > 0 ? conversions : null;
    }

    static labelForSingleEUKey(euKey, system) {
      // Lazy-load dynamic conversions from the size guide table (once).
      if (VariantPicker._dynamicSizeConversions === null) {
        const parsed = VariantPicker.parseSizeGuideTable();
        VariantPicker._dynamicSizeConversions = parsed || false;
      }

      // Try dynamic conversions first (from the size guide table).
      if (VariantPicker._dynamicSizeConversions) {
        const dynamicRow = VariantPicker._dynamicSizeConversions[euKey];
        if (dynamicRow) {
          return dynamicRow[system] || dynamicRow.EU || euKey;
        }
      }

      // Fallback to hardcoded SIZE_CONVERSIONS.
      const row = SIZE_CONVERSIONS[euKey];
      if (!row) return euKey;
      return row[system] || row.EU || euKey;
    }

    static isKidsEURange(parsed) {
      const end = parseFloat(parsed.end);
      return !isNaN(end) && end <= KIDS_MAX_EU;
    }

    static isKidsEUSize(euKey) {
      const n = parseFloat(euKey);
      return !isNaN(n) && n <= KIDS_MAX_EU_SINGLE;
    }

    // Drops any trailing "Y" (old kids notation) or an already-applied "K", so the
    // suffix is never doubled no matter where the raw label came from.
    static stripKidsSuffix(label) {
      return String(label).replace(/\s*[YK]$/i, '');
    }

    static labelForEUValue(src, system, rangesOn) {
      const parsed = VariantPicker.parseEUFromValue(src);
      if (!parsed) return src;

      if (parsed.isRange) {
        if (rangesOn) {
          let left = VariantPicker.labelForSingleEUKey(parsed.start, system);
          let right = VariantPicker.labelForSingleEUKey(parsed.end, system);
          const sep = parsed.sep || '-';

          // Kids sock ranges in US carry a single trailing "K" (5.5-8K), never a per-size "Y".
          if (system === 'US' && VariantPicker.isKidsEURange(parsed)) {
            left = VariantPicker.stripKidsSuffix(left);
            right = VariantPicker.stripKidsSuffix(right);
            return `${left}${sep}${right}${KIDS_US_SUFFIX}`;
          }

          return `${left}${sep}${right}`;
        }
        return VariantPicker.labelForSingleEUKey(parsed.start, system);
      }

      const label = VariantPicker.labelForSingleEUKey(parsed.key, system);

      // Kids shoe sizes in US carry a trailing "K" (EU 21 → 5.5K, EU 32 → 1K).
      if (system === 'US' && VariantPicker.isKidsEUSize(parsed.key)) {
        return VariantPicker.stripKidsSuffix(label) + KIDS_US_SUFFIX;
      }

      return label;
    }
  }

  customElements.define('variant-picker', VariantPicker);

  // Reset dynamic size conversion cache when a section is reloaded in the theme editor.
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => {
      VariantPicker._dynamicSizeConversions = null;
    });
  }
}